/**
 * Security Status Widget
 * 
 * Compact security status display for embedding in dashboards
 * and other application areas.
 */

import React, { useState, useEffect } from 'react';
import { logger } from '../../utils/logger';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Collapse
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Monitor as MonitorIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roleHelpers';

interface SecurityStatus {
  overall: 'secure' | 'warning' | 'critical';
  mfa: boolean;
  encryption: boolean;
  monitoring: boolean;
  audit: boolean;
  lastUpdate: string;
  activeThreats: number;
  securityScore: number;
  alerts?: Array<{
    severity: string;
    message: string;
    timestamp: string;
  }>;
  metrics?: any;
}

const SecurityStatusWidget: React.FC<{
  compact?: boolean;
  showDetails?: boolean;
}> = ({ compact = false, showDetails = false }) => {
  const { user, isLoading: authLoading } = useAuth();
  /**
   * Admin-only API. Compact usages (public footer + dashboard chip) must never
   * call it — guests get 401 and signed-in admins still poll on every page.
   */
  const canFetchAdminMetrics =
    !compact && !authLoading && hasRole(user, 'admin');

  const [securityStatus, setSecurityStatus] = useState<SecurityStatus>({
    overall: 'secure',
    mfa: true,
    encryption: true,
    monitoring: true,
    audit: true,
    lastUpdate: new Date().toISOString(),
    activeThreats: 0,
    securityScore: 95
  });
  
  const [expanded, setExpanded] = useState(showDetails);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!canFetchAdminMetrics) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchSecurityStatus = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/api/admin/security/metrics');
        if (response.ok) {
          const result = await response.json();
          if (!cancelled && result.success) {
            setSecurityStatus(result.data);
          }
        }
      } catch (error) {
        logger.warn('Failed to fetch security metrics', {
          error: error instanceof Error ? error.message : String(error),
        });
        // Keep default status on error
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchSecurityStatus();

    // Refresh every 5 minutes
    const interval = setInterval(() => {
      void fetchSecurityStatus();
    }, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [canFetchAdminMetrics]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'secure': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'secure': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'critical': return <ErrorIcon />;
      default: return <SecurityIcon />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'secure': return 'All Systems Secure';
      case 'warning': return 'Minor Issues Detected';
      case 'critical': return 'Critical Issues Detected';
      default: return 'Status Unknown';
    }
  };

  if (compact) {
    return (
      <Tooltip title={getStatusText(securityStatus.overall)}>
        <Chip
          icon={getStatusIcon(securityStatus.overall)}
          label={getStatusText(securityStatus.overall)}
          color={getStatusColor(securityStatus.overall)}
          variant="outlined"
        />
      </Tooltip>
    );
  }

  if (isLoading) {
    return (
      <Card sx={{ minWidth: 300 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SecurityIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Security Status</Typography>
          </Box>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading security status...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ minWidth: 300 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Security Status</Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Overall Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Chip
            icon={getStatusIcon(securityStatus.overall)}
            label={getStatusText(securityStatus.overall)}
            color={getStatusColor(securityStatus.overall)}
            sx={{ mr: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            Score: {securityStatus.securityScore}/100
          </Typography>
        </Box>

        {/* Security Score Progress */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Security Score
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={securityStatus.securityScore} 
            color={securityStatus.securityScore >= 90 ? 'success' : securityStatus.securityScore >= 70 ? 'warning' : 'error'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Quick Status Indicators */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Multi-Factor Authentication">
            <Chip
              icon={<LockIcon />}
              label="MFA"
              color={securityStatus.mfa ? 'success' : 'error'}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Data Encryption">
            <Chip
              icon={<ShieldIcon />}
              label="Encryption"
              color={securityStatus.encryption ? 'success' : 'error'}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Security Monitoring">
            <Chip
              icon={<MonitorIcon />}
              label="Monitoring"
              color={securityStatus.monitoring ? 'success' : 'error'}
              size="small"
            />
          </Tooltip>
        </Box>

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Security Details
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Active Threats:</Typography>
              <Typography variant="body2" color={securityStatus.activeThreats === 0 ? 'success.main' : 'error.main'}>
                {securityStatus.activeThreats}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Last Update:</Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(securityStatus.lastUpdate).toLocaleTimeString()}
              </Typography>
            </Box>

            {/* Security Alerts */}
            {securityStatus.activeThreats > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {securityStatus.activeThreats} active security threat{securityStatus.activeThreats > 1 ? 's' : ''} detected
                </Typography>
              </Alert>
            )}

            {securityStatus.overall === 'secure' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  All security systems operational. No threats detected.
                </Typography>
              </Alert>
            )}
          </Box>
        </Collapse>

        {/* Last Updated */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Last updated: {new Date(securityStatus.lastUpdate).toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SecurityStatusWidget;
